import { hhmm } from '@limuzin/kit';
import type { DriverLine, PassengerRideView } from '@limuzin/passenger';

/**
 * The night screen's words — `night_screen.dart` in the phone app, sentence for
 * sentence, so the same town at the same hour reads the same in a browser.
 *
 * Whether it shows at all follows who is on the line, which the server says; only the
 * words and the offer of a morning car follow the clock. It never says the service is
 * closed or names hours, because a driver may yet come on.
 */
export interface NightReading {
  line: Exclude<DriverLine, 'free'>;
  title: string;
  lead: string;
  /** What «Ищем водителя» keeps under its stage once the passenger goes on looking. */
  note: string;
  night: boolean;
  /** The car to offer instead, at night; null by day. */
  morning: Date | null;
}

/** When drivers here are usually off the line: on at seven, off at ten at night. */
export const nightHours = (now: Date): boolean => now.getHours() >= 22 || now.getHours() < 7;

/** Half past seven — today before then, tomorrow after. */
export function morningAfter(now: Date): Date {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 7, 30);
  if (now < today) return today;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 7, 30);
}

/** «Сегодня, 07:30» or «Завтра, 07:30». */
export const morningLabel = (morning: Date, now: Date): string =>
  `${morning.getDate() === now.getDate() ? 'Сегодня' : 'Завтра'}, ${hhmm(morning)}`;

const STAYS = 'Заказ останется в поиске — его увидит первый, кто выйдет на линию.';
const LONGER = 'Поиск может занять больше времени.';

export function nightReading(ride: PassengerRideView, now: Date): NightReading | null {
  const line = ride.line;
  if (!line || line === 'free') return null;
  const night = nightHours(now);
  const morning = night ? morningAfter(now) : null;
  if (line === 'empty') {
    return {
      line,
      title: 'Сейчас нет свободных водителей',
      lead: night
        ? `Ночью водители выходят на линию реже. ${STAYS}`
        : `Сейчас на линии никого нет. ${STAYS}`,
      note: 'Сейчас нет водителей на линии — ждём, пока кто-нибудь выйдет',
      night,
      morning,
    };
  }
  return {
    line,
    title: 'Сейчас мало водителей',
    lead: night
      ? `Ночью водители выходят на линию реже. ${LONGER}`
      : `Все водители сейчас на заказах. ${LONGER}`,
    note: 'Сейчас мало водителей — поиск может занять дольше',
    night,
    morning,
  };
}

const WEIGHT: Record<DriverLine, number> = { free: 0, busy: 1, empty: 2 };

/** Only worse news comes back after «Продолжить поиск»: nobody, after few. */
export const lineIsNews = (line: DriverLine, seen: DriverLine | undefined): boolean =>
  WEIGHT[line] > (seen ? WEIGHT[seen] : 0);

/**
 * The line each order's night screen was answered at with «Продолжить поиск». Held for
 * the life of the page, above the ride tab, so leaving the tab and coming back does
 * not put the same news in front of the passenger again; a reload shows it once more.
 */
export const lineSeen = new Map<string, DriverLine>();
